import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Trophy,
  Users,
  ShieldCheck,
  Sparkles,
  Loader2,
  ArrowLeft,
  Copy,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  draft: { label: "Taslak", color: "bg-[#E8E2D5] text-[#1B2A22]" },
  frozen: { label: "Katılımcılar Dondu", color: "bg-[#E5A024]/30 text-[#B4711C]" },
  drawn: { label: "🎉 Kazananlar Belli", color: "bg-[#2E5A36] text-[#FAF7F2]" },
};

export default function RafflePublic() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/raffles/public/${slug}`);
        setRaffle(data);
      } catch (e) {
        setRaffle(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const copyShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Bağlantı kopyalandı");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E5A36]" />
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white border border-[#E8E2D5] rounded-2xl p-8 max-w-md text-center">
            <h1 className="font-heading font-bold text-xl text-[#1B2A22]">
              🎁 Çekiliş bulunamadı
            </h1>
            <p className="text-sm text-[#1B2A22]/60 mt-2">
              Bu bağlantı geçersiz olabilir.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="mt-4 rounded-full bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2]"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Ana sayfa
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const meta = STATUS_MAP[raffle.status] || STATUS_MAP.draft;
  const isDrawn = raffle.status === "drawn";
  const rules = raffle.rules || {};
  const ruleLines = [];
  if (rules.date_from || rules.date_to)
    ruleLines.push(
      `Sipariş tarihi: ${rules.date_from || "—"} ↔ ${rules.date_to || "—"}`
    );
  if (rules.min_order_count != null)
    ruleLines.push(`Minimum ${rules.min_order_count} sipariş`);
  if (rules.min_total_spent != null)
    ruleLines.push(`Minimum ₺${Number(rules.min_total_spent).toFixed(0)} harcama`);
  if (ruleLines.length === 0) ruleLines.push("Tüm kayıtlı müşteriler");

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#D9653B]/10 text-[#B4522F] font-heading font-bold rounded-full px-4 py-1.5 text-sm mb-4">
            <Trophy className="w-4 h-4" /> İkizler Market Çekilişi
          </div>
          <h1
            data-testid="raffle-title"
            className="font-heading font-extrabold text-3xl sm:text-5xl text-[#1B2A22]"
          >
            {raffle.title}
          </h1>
          {raffle.description && (
            <p className="text-[#1B2A22]/70 mt-3 max-w-2xl mx-auto">
              {raffle.description}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            <Badge className={`${meta.color} border-0 font-heading`}>
              {meta.label}
            </Badge>
            {raffle.prize && (
              <Badge className="bg-[#E5A024] text-white border-0 font-heading">
                🎁 {raffle.prize}
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={copyShare}
              className="rounded-full ml-1"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Bağlantıyı Paylaş
            </Button>
          </div>
        </div>

        {/* Winners section */}
        {isDrawn && (
          <div
            data-testid="raffle-winners"
            className="bg-gradient-to-br from-[#2E5A36] to-[#1B2A22] text-[#FAF7F2] rounded-3xl p-8 shadow-xl mb-6"
          >
            <div className="text-center mb-6">
              <Sparkles className="w-8 h-8 text-[#E5A024] mx-auto mb-2" />
              <h2 className="font-heading font-extrabold text-2xl">
                Kazanan{raffle.winners.length > 1 ? "lar" : ""}
              </h2>
              <p className="text-sm opacity-70 mt-1">
                {new Date(raffle.drawn_at).toLocaleString("tr-TR")}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {raffle.winners.map((w) => (
                <div
                  key={w}
                  data-testid={`raffle-winner-${w}`}
                  className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4 text-center"
                >
                  <Trophy className="w-6 h-6 text-[#E5A024] mx-auto mb-1" />
                  <p className="text-xs uppercase font-heading font-bold opacity-70">
                    Müşteri No
                  </p>
                  <p className="font-mono font-extrabold text-3xl">
                    #{String(w).padStart(4, "0")}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rules */}
        <div className="bg-white border border-[#E8E2D5] rounded-2xl p-6 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-[#2E5A36]" />
            <h3 className="font-heading font-bold text-lg">
              Katılım Koşulları
            </h3>
          </div>
          <ul className="text-sm text-[#1B2A22]/80 list-disc pl-5 space-y-1">
            {ruleLines.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </div>

        {/* Transparency block */}
        {raffle.seed && (
          <div
            data-testid="raffle-transparency"
            className="bg-white border-2 border-dashed border-[#2E5A36]/40 rounded-2xl p-6 mb-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-[#2E5A36]" />
              <h3 className="font-heading font-bold text-lg">
                Şeffaflık — Doğrulanabilir Çekiliş
              </h3>
            </div>
            <p className="text-sm text-[#1B2A22]/80 mb-3">
              Kazanan tamamen deterministik olarak seçildi. Aşağıdaki tohum
              (seed) + katılımcı listesi ile aynı sonucu her yerde
              üretebilirsiniz. Yönetici, çekiliş öncesinde kazananı bilemez.
            </p>
            <div className="bg-[#FAF7F2] rounded-xl p-3 font-mono text-xs break-all">
              <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36] mb-1">
                Public Seed
              </p>
              <p data-testid="raffle-seed">{raffle.seed}</p>
            </div>
          </div>
        )}

        {/* Eligibles */}
        <div className="bg-white border border-[#E8E2D5] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#2E5A36]" />
              <h3 className="font-heading font-bold text-lg">
                Katılımcılar ({raffle.eligibles.length})
              </h3>
            </div>
            {raffle.winner_count > 1 && (
              <p className="text-xs text-[#1B2A22]/60">
                {raffle.winner_count} kişi kazanacak
              </p>
            )}
          </div>
          {raffle.eligibles.length === 0 ? (
            <p className="text-sm text-[#1B2A22]/50 text-center py-6">
              Katılımcılar henüz dondurulmadı.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {raffle.eligibles.map((c) => {
                const isWinner = raffle.winners?.includes(c);
                return (
                  <span
                    key={c}
                    data-testid={`raffle-eligible-${c}`}
                    className={`inline-flex items-center gap-1 font-mono font-bold text-sm px-3 py-1.5 rounded-full border ${
                      isWinner
                        ? "bg-[#E5A024] text-white border-[#E5A024] ring-2 ring-[#E5A024]/40"
                        : "bg-[#FAF7F2] text-[#1B2A22] border-[#E8E2D5]"
                    }`}
                  >
                    {isWinner && <Trophy className="w-3 h-3" />}#
                    {String(c).padStart(4, "0")}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
